import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MessageAttribution, StepPresence as StepPresenceSnapshot } from '@reef/api-contract';
import { request } from '../http.ts';

/**
 * Who else is in this step conversation, and who sent which message.
 *
 * A step chat has been single-player: one composer, one transcript, and no way
 * to tell a message you sent from one a colleague sent an hour ago. The
 * conversation was already shared — anyone with `read` on the workflow could
 * open it — so what was missing was not access but the two things that make a
 * shared surface usable: knowing somebody else is here, and knowing who said
 * what.
 *
 * **The transport is `request` directly rather than a method on `api.ts`.**
 * These three endpoints have exactly one caller, the step chat, and the studio's
 * rule is that a method on the shared client earns its place when more than one
 * screen wants it. What matters is that it goes through `request` at all: the
 * 401 handling, the error unwrapping and the typed refusals are the reason
 * there is one transport.
 *
 * **Presence is a beat, not a flag.** The server decides who is here by
 * comparing the last beat against a window it owns, and it tells us how often to
 * beat, so the interval lives in one place. Nothing here has to remember to
 * announce a departure for the list to be right — a closed laptop stops beating,
 * and the row ages out. The explicit leave on unmount is a courtesy that makes
 * the common case immediate rather than a correctness requirement, which is why
 * it is fire-and-forget.
 */
export function usePresence(conversationId: string) {
  const [presence, setPresence] = useState<StepPresenceSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * The beat interval, held in a ref so the effect below does not restart every
   * time a beat comes back — restarting it would reset the timer on every beat
   * and, in a conversation somebody else is also beating into, could stop it
   * landing at all.
   */
  const beatMs = useRef(DEFAULT_BEAT_MS);

  const beat = useCallback(async (): Promise<StepPresenceSnapshot | null> => {
    try {
      const snapshot = await request<StepPresenceSnapshot>('/collaborate/presence', {
        method: 'POST',
        body: JSON.stringify({ conversationId }),
      });
      beatMs.current = snapshot.beatEveryMs > 0 ? snapshot.beatEveryMs : DEFAULT_BEAT_MS;
      setPresence(snapshot);
      setError(null);
      return snapshot;
    } catch (caught) {
      /*
       * A failed beat is not a banner over the transcript. The chat still works
       * — presence is the thing that has gone stale — and a step whose
       * participant list cannot be read is not a step you should be stopped
       * from reading. The message is kept so the bar can say it quietly.
       */
      setError((caught as Error).message);
      return null;
    }
  }, [conversationId]);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loop = async () => {
      if (!live) return;
      await beat();
      if (!live) return;
      timer = setTimeout(() => void loop(), beatMs.current);
    };

    void loop();

    return () => {
      live = false;
      if (timer) clearTimeout(timer);
      // Best-effort: the window decays on its own, so a leave lost to a closing
      // tab costs one interval of staleness rather than a stuck participant.
      void request('/collaborate/presence/leave', {
        method: 'POST',
        body: JSON.stringify({ conversationId }),
      }).catch(() => {});
    };
  }, [beat, conversationId]);

  /**
   * Claim the message about to be sent.
   *
   * Called *before* the prompt reaches the agent, because the claim is what
   * decides who the step is acting as for everything that message causes —
   * including whose name goes on a commit. A send that raced ahead of its claim
   * would be attributed to whoever spoke last, which is the one outcome worth
   * failing to avoid: a wrong name is worse than no name.
   *
   * The digest is computed here and the text is not sent. Reef does not need
   * the message to know who sent it, and a second copy of every prompt in the
   * control plane would be a second thing to redact and retain.
   */
  const claimTurn = useCallback(
    async (text: string, ordinal: number): Promise<MessageAttribution> => {
      const claimed = await request<{ claim: MessageAttribution }>('/collaborate/presence/turn', {
        method: 'POST',
        body: JSON.stringify({ conversationId, ordinal, digest: await sha256Hex(text) }),
      });
      // Fold the claim in immediately so the message renders with its author
      // rather than appearing unattributed until the next beat.
      setPresence((current) =>
        current
          ? { ...current, attributions: [...current.attributions, claimed.claim] }
          : current,
      );
      return claimed.claim;
    },
    [conversationId],
  );

  return { presence, presenceError: error, claimTurn, refreshPresence: beat };
}

/**
 * The header line: how many people are here, and who.
 *
 * People who are here are named; people who have been here are counted. The
 * distinction is the whole point of the feature — a list that shows everybody
 * who ever opened the step with equal weight is a list that answers "is anyone
 * else looking at this" with "maybe".
 */
export function PresenceBar({
  presence,
  error,
}: {
  presence: StepPresenceSnapshot | null;
  error: string | null;
}) {
  if (!presence) {
    return <span className="hint">{error ? 'Presence unavailable' : 'Joining…'}</span>;
  }

  const here = presence.participants.filter((participant) => participant.here);
  const past = presence.participants.length - here.length;

  return (
    <span className="hint" title={describe(presence)}>
      {here.length === 0
        ? 'Nobody else here'
        : `${here.length} here: ${here.map((participant) => label(participant)).join(', ')}`}
      {past > 0 && ` · ${past} earlier`}
    </span>
  );
}

/**
 * Which claim, if any, labels each user message.
 *
 * A claim carries an ordinal and a digest, and a message is labelled only when
 * both agree with what is being rendered. That second condition is what makes
 * this safe rather than approximate: two people sending at the same instant
 * compute the same ordinal, one claim overwrites the other, and the digest is
 * how the survivor is prevented from labelling the wrong message. An
 * unattributed message is a small gap; a message credited to somebody who did
 * not send it is a false record, and it is the record a commit's author is
 * derived from.
 *
 * Hashing happens in an effect rather than during render because it is async —
 * and it is keyed on the texts themselves, so a transcript that has not changed
 * is not rehashed on every beat.
 */
export function useMessageAuthors(
  userMessages: readonly string[],
  attributions: readonly MessageAttribution[],
): Map<number, MessageAttribution> {
  const [digests, setDigests] = useState<string[]>([]);
  const key = userMessages.join(' ');

  useEffect(() => {
    let live = true;
    void Promise.all(userMessages.map((text) => sha256Hex(text))).then((hashed) => {
      if (live) setDigests(hashed);
    });
    return () => {
      live = false;
    };
    /*
     * Keyed on the joined texts rather than on the array: a new array with the
     * same contents arrives on every beat, and depending on its identity would
     * rehash the whole transcript twice a minute for nothing.
     */
  }, [key]);

  return useMemo(() => {
    const byOrdinal = new Map<number, MessageAttribution>();
    for (const attribution of attributions) byOrdinal.set(attribution.ordinal, attribution);

    const authors = new Map<number, MessageAttribution>();
    digests.forEach((digest, ordinal) => {
      const claim = byOrdinal.get(ordinal);
      if (claim && claim.digest === digest) authors.set(ordinal, claim);
    });
    return authors;
  }, [attributions, digests]);
}

/** What to call somebody: their address, their name, or — failing both — their id. */
export function label(participant: {
  email: string | null;
  name: string | null;
  userId: string;
  you?: boolean;
}): string {
  const named = participant.email ?? participant.name ?? participant.userId;
  return participant.you ? `${named} (you)` : named;
}

function describe(presence: StepPresenceSnapshot): string {
  return presence.participants
    .map(
      (participant) =>
        `${label(participant)} — ${participant.here ? 'here' : 'last seen ' + participant.lastSeenAt}` +
        `, ${participant.messages} message${participant.messages === 1 ? '' : 's'}`,
    )
    .join('\n');
}

/**
 * The same digest the server stores, computed over the same bytes.
 *
 * SubtleCrypto rather than a hand-rolled hash because the studio is served over
 * HTTPS and this is one call; the only requirement is that both sides agree,
 * and "lowercase hex SHA-256 of the UTF-8 text" is the agreement.
 */
async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Used only until the first beat answers with the interval the server wants. */
const DEFAULT_BEAT_MS = 30_000;
